import { IEmployee } from '@/types/employer';
import { ITeam } from '@/types/team';

import { uuidToStableIntegerId } from '@/modules/common/lib/id';
import { Delete, Edit, ExpandMore, Visibility } from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  Avatar,
  AvatarGroup,
  Button,
  ButtonGroup,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import pluralize from 'pluralize';
import { useState } from 'react';
import { colorPalette } from '../../../../theme/colors';

type GetListTeamsTableColumnsOpts = {
  onManageLearningBudgetFunds?: (team: ITeam) => void;
  onDeleteTeam?: (team: ITeam) => void;
  onEditTeam?: (team: ITeam) => void;
};

export const getListTeamsTableColumns = ({
  onManageLearningBudgetFunds,
  onDeleteTeam,
  onEditTeam,
}: GetListTeamsTableColumnsOpts): MRT_ColumnDef<ITeam>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    muiTableHeadCellProps: {
      className: 'pl-14',
    },
    Cell: ({ row: { original } }) => {
      const members = (original.members ?? []) as IEmployee[];

      const colors = [
        colorPalette.tag.yellow,
        colorPalette.tag.blue,
        colorPalette.tag.orange,
        colorPalette.info.dark,
        colorPalette.info.main,
        colorPalette.info.light,
      ];

      const colorForId =
        colors[uuidToStableIntegerId(original._id, colors.length)] ??
        colorPalette.info.main;

      return (
        <div className="flex max-w-full items-center text-wrap break-words gap-2">
          <div
            className="w-10 h-10 rounded-full shrink-0"
            style={{ backgroundColor: colorForId }}
          />
          <Stack>
            <Typography className="capitalize font-semibold">
              {original.name}
            </Typography>
            {members.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {pluralize('member', members.length, true)}
              </Typography>
            )}
          </Stack>
        </div>
      );
    },
    grow: 1,
  },
  {
    header: 'Employees',
    accessorKey: 'members',
    Cell: ({ row: { original } }) => {
      const members = (original.members ?? []) as IEmployee[];
      if (!members.length) {
        return (
          <Typography
            variant="body2"
            className="max-w-full text-wrap break-words"
          >
            No members
          </Typography>
        );
      }

      return (
        <div className="flex gap-1 flex-col max-w-full text-wrap break-words">
          <AvatarGroup
            total={members.length}
            spacing="medium"
            max={4}
            classes={{
              avatar: 'w-6 h-6 text-primary bg-divider text-sm',
              root: 'flex-row',
            }}
          >
            {members.map(({ _id, firstName, lastName }) => {
              const fullNameParts = [firstName, lastName].filter(Boolean);
              const initials = fullNameParts
                .map(name => name?.[0])
                .join('')
                .toUpperCase();
              const fullName = fullNameParts.join(' ');

              return (
                <Tooltip key={_id} title={fullName}>
                  <Avatar className="w-6 h-6" key={_id} alt={fullName}>
                    {initials}
                  </Avatar>
                </Tooltip>
              );
            })}
          </AvatarGroup>
        </div>
      );
    },
    grow: 1,
  },
  {
    grow: 0,
    id: 'actions',
    header: 'Actions',
    muiTableHeadCellProps: {
      align: 'right',
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
    Cell: ({ row: { original: team } }) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
      const isOpen = Boolean(anchorEl);

      const showMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
      };

      const hideMenu = () => {
        setAnchorEl(null);
      };

      return (
        <>
          <ButtonGroup
            disableElevation
            variant="outlined"
            className="flex-nowrap"
            size="small"
            aria-label="employee actions"
          >
            <Button
              href={`/teams/${team._id}`}
              variant="outlined"
              sx={{
                backgroundColor: 'white',
              }}
              className="rounded-r-none border-lines"
            >
              View
            </Button>
            <Button
              size="small"
              onClick={showMenu}
              aria-controls={isOpen ? 'employee-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isOpen ? 'true' : undefined}
              className="rounded-l-none bg-gray-background border-dividers"
            >
              <ExpandMore />
            </Button>
          </ButtonGroup>
          <Menu
            id={`employee-actions-menu-${team._id}`}
            anchorEl={anchorEl}
            open={isOpen}
            onClose={hideMenu}
            slotProps={{
              list: {
                'aria-labelledby': 'course-actions-button',
              },
            }}
          >
            <MenuItem
              className="border-b last-of-type:border-none"
              onClick={() => {
                onEditTeam?.(team);
                hideMenu();
              }}
            >
              <div className="flex gap-2">
                <span className="text-sm">
                  <Edit fontSize="inherit" color="inherit" />
                </span>
                Edit team
              </div>
            </MenuItem>
            <MenuItem
              className="border-b last-of-type:border-none"
              onClick={() => {
                onManageLearningBudgetFunds?.(team);
                hideMenu();
              }}
            >
              <div className="flex gap-2">
                <span className="text-sm">
                  <AccountBalanceWalletOutlinedIcon
                    fontSize="inherit"
                    color="inherit"
                  />
                </span>
                Manage LB funds
              </div>
            </MenuItem>
            <MenuItem
              className="border-b last-of-type:border-none"
              href={`/learning-budgets/transactions?teamId=${team._id}`}
            >
              <Link
                href={`/learning-budgets/transactions?teamId=${team._id}`}
                className="no-underline"
              >
                <div className="flex gap-2">
                  <span className="text-sm">
                    <Visibility fontSize="inherit" color="inherit" />
                  </span>
                  View LB transactions
                </div>
              </Link>
            </MenuItem>
            <MenuItem
              className="border-b last-of-type:border-none"
              onClick={() => {
                onDeleteTeam?.(team);
                hideMenu();
              }}
            >
              <div className="flex gap-2">
                <span className="text-sm">
                  <Delete fontSize="inherit" color="inherit" />
                </span>
                Delete
              </div>
            </MenuItem>
          </Menu>
        </>
      );
    },
  },
];
